import React, { useCallback } from 'react';
import API from '../../common/api.js';
import TaskForm from './taskform.js';
import useStateObject from '../../common/useStateObject.js';
import Dropdown from 'react-dropdown';
import 'react-dropdown/style.css';


function triState(v) {
    if (v === null) return null;
    if (v.value === "Yes") return true;
    if (v.value === "No") return false;
    return null;
}

function ClaimReopenRequired({caseNumber, username, initialFormState, markTaskDone, completedAt, markTaskDoing}) {
    const [formState, updateFormState] = useStateObject(JSON.parse(initialFormState) || {
        canReopen: null,
        canReopenReason: "",
        withClient: null,
        withClientReason: "",
        explained: null,
        explainedReason: "",
        workAndEarningsReported: null,
        workAndEarnings: "",
        backdatingNecessary: null,
        furtherCertificationsAvailable: null,
        notes: ""
    });

    const canReopen = triState(formState.canReopen);
    const withClient = triState(formState.withClient);
    const explained = triState(formState.explained);
    const workAndEarningsReported = triState(formState.workAndEarningsReported);
    const backdatingNecessary = triState(formState.backdatingNecessary);
    const furtherCertificationsAvailable = triState(formState.furtherCertificationsAvailable);
    
    const saveForm = useCallback(async (matterID) => {
        return await API.saveClaimReopenRequired(
                canReopen, formState.canReopenReason, 
                withClient, formState.withClientReason, 
                explained, formState.explainedReason,
                workAndEarningsReported, formState.workAndEarnings,
                backdatingNecessary,
                furtherCertificationsAvailable,
                formState.notes, username, matterID);
    }, [canReopen, formState.canReopenReason, withClient, formState.withClientReason, explained, formState.explainedReason, workAndEarningsReported, formState.workAndEarnings, backdatingNecessary, furtherCertificationsAvailable, formState.notes, username]);

    const yesNoOptions = ["Yes", "No"];
        
    const canReopenDropdown = (
        <div className="field eight wide">
            <label htmlFor="can_reopen">1. Are you able to re-open this claim?</label>
            <Dropdown placeholder="Please select" options={yesNoOptions} onChange={option => updateFormState({canReopen: option})} value={formState.canReopen} required/>
        </div>
    );

    const canReopenTextfield = (
        <div className="field eight wide">
            <label htmlFor="can_reopen_reason">Why not?</label>
            <input type="text" name="can_reopen_reason" value={formState.canReopenReason} placeholder="Explain why the claim can't be re-opened" onChange={ev => updateFormState({canReopenReason: ev.target.value})} required/>
        </div>
    );

    const withClientDropdown = (
        <div className="field eight wide">
            <label htmlFor="with_client">2. Is the re-open performed with the client on the phone, with the client's understanding and at the client's request for assistance?</label>
            <Dropdown placeholder="Please select" options={yesNoOptions} onChange={option => updateFormState({withClient: option})} value={formState.withClient} required/>
        </div>
    );

    const withClientTextfield = (
        <div className="field eight wide">
            <label htmlFor="with_client_reason">Why not?</label>
            <input type="text" name="with_client_reason" value={formState.withClientReason} onChange={ev => updateFormState({withClientReason: ev.target.value})} placeholder="Explain why not" required/>
        </div>
    );

    const explainedDropdown = (
        <div className="field eight wide">
            <label for="explained">3. Did you read and explain all written content to the client?</label>
            <Dropdown placeholder="Please select" options={yesNoOptions} onChange={option => updateFormState({explained: option})} value={formState.explained} required/>
        </div>
    );

    const explainedTextfield = (
        <div className="field eight wide">
            <label htmlFor="explained_reason">Why not?</label>
            <input type="text" name="explained_reason" value={formState.explainedReason} placeholder="Explain why not" onChange={ev => updateFormState({explainedReason: ev.target.value})} required/>
        </div>
    );

    const workAndEarningsReportedDropdown = (
        <div className="field eight wide">
            <label htmlFor="work_and_earnings_reported">4. Did the client report any work and earnings?</label>
            <Dropdown placeholder="Please select" options={yesNoOptions} onChange={option => updateFormState({workAndEarningsReported: option})} value={formState.workAndEarningsReported} required/>
        </div>
    );

    const workAndEarningsTextfield = (
        <div className="field eight wide">
            <label htmlFor="work_and_earnings">Client report</label>
            <textarea type="text" name="work_and_earnings" rows="4" value={formState.workAndEarnings} placeholder="Enter client report" onChange={ev => updateFormState({workAndEarnings: ev.target.value})} required></textarea>
        </div>
    );

    const backdatingNecessaryDropdown = (
        <div className="field eight wide">
            <label htmlFor="backdating_necessary">5. Is it necessary to backdate certifications?</label>
            <Dropdown placeholder="Please select" options={yesNoOptions} onChange={option => updateFormState({backdatingNecessary: option})} value={formState.backdatingNecessary} required/>
        </div>
    );

    const furtherCertificationsAvailableDropdown = (
        <div className="field eight wide">
            <label for="further_certifications_available">6. Are further certifications currently available?</label>
            <Dropdown placeholder="Please select" options={yesNoOptions} onChange={option => updateFormState({furtherCertificationsAvailable: option})} value={formState.furtherCertificationsAvailable} required/>
        </div>
    );

    const warningBox = (
        <div className="ui red message">
            <div className="ui grid">
                <div className="one wide column">
                    <i className="icon huge exclamation triangle"/>
                </div>
                <div className="fifteen wide column" style={{paddingLeft: 30}}>
                    Never submit a certification until you made sure the client completely understands the submission and that you do so with the client's consent and understanding.
                </div>
            </div>
        </div>
    );

    const notesTextfield = (
        <div className="field eight wide">
            <label htmlFor="notes">Optional Notes</label>
            <textarea type="text" name="notes" rows="4" value={formState.notes} placeholder="Enter notes..." onChange={ev => updateFormState({notes: ev.target.value})}></textarea>
        </div>
    );

    function formComplete() {
        if (canReopen === false && formState.canReopenReason !== "") return true;
        if (withClient === false && formState.withClientReason !== "") return true;
        if (explained === false && formState.explainedReason !== "") return true;
        if (workAndEarningsReported && formState.workAndEarnings === "") return false;
        if (canReopen && withClient && explained && workAndEarningsReported !== null && backdatingNecessary !== null && furtherCertificationsAvailable !== null) return true;
        return false;
    }

    return (
        <TaskForm caseNumber={caseNumber} saveButtonText="Save and Submit" disableSaveButton={!formComplete()} saveForm={saveForm} markTaskDone={markTaskDone} markTaskDoing={markTaskDoing} formState={formState} completedAt={completedAt}>
            <p>Please re-open the EDD online account. Note: <b>NEVER</b> complete this step without the Client on the phone.</p>
            {canReopenDropdown}
            {canReopen === false && canReopenTextfield}

            {canReopen && withClientDropdown}
            {canReopen && withClient === false && withClientTextfield}
            {canReopen && withClient === false && warningBox}
            
            {canReopen && withClient && explainedDropdown}
            {canReopen && withClient && explained === false && explainedTextfield}
            {canReopen && withClient && explained === false && warningBox}

            {canReopen && withClient && explained && workAndEarningsReportedDropdown}
            {canReopen && withClient && explained && workAndEarningsReported === true && workAndEarningsTextfield}

            {canReopen && withClient && explained && workAndEarningsReported !== null && backdatingNecessaryDropdown}

            {canReopen && withClient && explained && workAndEarningsReported !== null && furtherCertificationsAvailableDropdown}

            <div className="ui divider"/>
            {notesTextfield}
        </TaskForm>
    );
}

export default ClaimReopenRequired;
